// Órdenes en 4 pasos de Farmacia Interna (Fase 3-4, 2026-09-11) — Crear
// (borrador) → Firmar → Aplicar stock → Vender, mismo motor que
// /admin/cobrar del CRM (lib/billing.ts + lib/ventas.ts:venderOrden), pero
// acotado a origenModulo==='farmacia' (http-routes/hce-farmacia-facturacion.ts).
// Único camino de venta de farmacia interna — "Vender" (venta directa) y
// "Dispensar" (desde la cola de recetas) se retiraron 2026-09-11.
//
// El selector de ítems para armar una orden reusa el patrón de carrito
// (buscar → agregar → quitar) que ya se introdujo en VentaDirecta.tsx a
// pedido de Sue, en vez de copiar el patrón viejo de "lista todo el
// catálogo con una cajita de cantidad al lado" que sigue usando
// la antigua pantalla de Órdenes.
//
// Paciente (lead) + recetas de la cola (2026-09-11): buscar primero por
// paciente, igual que en el resto del sistema de facturación — al elegir
// uno, sus perfiles de facturación guardados se sugieren para el comprador
// Y sus recetas electrónicas pendientes (MedicationRequest activas sin
// MedicationDispense) aparecen para agregar a la orden con un clic, mezcladas
// libremente con cualquier otro ítem del catálogo en la misma orden. Cada
// línea que viene de una receta queda tageada con su `medicationRequestRef`;
// al Vender, el backend verifica que esa receta no se haya vendido ya en
// otra orden y crea el `MedicationDispense` en el HCE del paciente — el
// registro clínico de "esto se dispensó" ya no es un paso aparte.
import type { MedicationDispense, MedicationRequest } from '@medplum/fhirtypes';
import { useSearchResources } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { listarPacientesHce, type PacienteHce } from '@/lib/hceVault';
import {
  listarCatalogoFarmacia,
  listarPerfilesFacturacion,
  listarStockItemsFarmacia,
  type BuyerIdTypeFarmacia,
  type CatalogoItemFarmacia,
  type FormaPagoParticular,
  type PerfilFacturacion,
  type StockItemFarmacia,
} from '@/lib/farmaciaInterna';
import {
  aplicarOrdenFarmacia,
  cancelarOrdenFarmacia,
  crearOrdenFarmacia,
  firmarOrdenFarmacia,
  listarOrdenesFarmacia,
  venderOrdenFarmacia,
  type OrdenFarmacia,
} from '@/lib/farmaciaInterna';
import { ResultadoVentaFarmacia, type ResultadoVenta } from './ResultadoVentaFarmacia';

function formatoUsd(cents: number): string {
  return (cents / 100).toLocaleString('es-EC', { style: 'currency', currency: 'USD' });
}

type LineaCarrito = { catalogItemId: number; cantidad: string; medicationRequestRef?: string };
type ComoPaga = 'particular' | 'copago' | 'reembolso' | 'carta';

function yaDispensada(receta: MedicationRequest, dispensaciones: MedicationDispense[]): boolean {
  return dispensaciones.some((md) => md.authorizingPrescription?.some((ref) => ref.reference === `MedicationRequest/${receta.id}`));
}

/**
 * true si `nombreCorto` (nombre del catálogo, ej. "Amoxicilina 500mg")
 * "calza" contra `textoLargo` (lo que se está buscando) — en cualquiera de
 * las dos direcciones, más un tercer caso: cuando `textoLargo` viene
 * precargado desde el texto LIBRE de una receta de Medplum
 * (`medicationCodeableConcept.text`, ej. "amoxicilina 500mg capsulas
 * capsula"), suele ser más verboso que el nombre corto del catálogo — un
 * simple `catálogo.includes(texto)` nunca matchea porque es al revés. Se
 * resuelve pidiendo que TODAS las palabras del nombre del catálogo
 * aparezcan sueltas dentro del texto buscado.
 */
function coincideCatalogo(nombreCorto: string, textoLargo: string): boolean {
  if (nombreCorto.includes(textoLargo) || textoLargo.includes(nombreCorto)) return true;
  const palabras = nombreCorto.split(/\s+/).filter(Boolean);
  return palabras.length > 0 && palabras.every((p) => textoLargo.includes(p));
}

/** Recetas activas sin dispensar de UN paciente — mismo criterio que ColaRecetas.tsx, acotado a su `medplumPatientId`. */
function useRecetasPendientes(medplumPatientId: string | undefined): MedicationRequest[] {
  const [recetas] = useSearchResources(
    'MedicationRequest',
    medplumPatientId ? { status: 'active', subject: `Patient/${medplumPatientId}`, _sort: '-authoredon' } : undefined,
  );
  const [dispensaciones] = useSearchResources(
    'MedicationDispense',
    medplumPatientId ? { subject: `Patient/${medplumPatientId}`, _count: 100 } : undefined,
  );
  if (!recetas || !dispensaciones) return [];
  return recetas.filter((mr) => !yaDispensada(mr, dispensaciones));
}

export function OrdenesFarmacia(): JSX.Element {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [catalogo, setCatalogo] = useState<CatalogoItemFarmacia[]>();
  const [disponibles, setDisponibles] = useState<StockItemFarmacia[]>([]);
  const [ordenes, setOrdenes] = useState<OrdenFarmacia[]>();
  const [disponible, setDisponible] = useState(true);

  const [lineas, setLineas] = useState<LineaCarrito[]>([]);
  const [busquedaItem, setBusquedaItem] = useState('');
  const [recetaEnCurso, setRecetaEnCurso] = useState<string>();
  const [buyerIdType, setBuyerIdType] = useState<BuyerIdTypeFarmacia>('consumidor_final');
  const [buyerIdNumber, setBuyerIdNumber] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState<string>();
  const [aviso, setAviso] = useState<string>();

  const [pacientes, setPacientes] = useState<PacienteHce[]>([]);
  const [busquedaPaciente, setBusquedaPaciente] = useState('');
  const [paciente, setPaciente] = useState<PacienteHce>();
  const [perfiles, setPerfiles] = useState<PerfilFacturacion[]>([]);
  const recetasPendientes = useRecetasPendientes(paciente?.medplumPatientId ?? undefined);
  const prefiltrado = useRef(false);

  type VentaPanel = {
    comoPaga: ComoPaga;
    metodoParticular: FormaPagoParticular;
    buyerName: string;
    montoRecibido: string;
    buyerIdType: BuyerIdTypeFarmacia;
    buyerIdNumber: string;
  };
  const [ventaAbierta, setVentaAbierta] = useState<number>();
  const [panelVenta, setPanelVenta] = useState<VentaPanel>({
    comoPaga: 'particular',
    metodoParticular: 'cash',
    buyerName: '',
    montoRecibido: '',
    buyerIdType: 'consumidor_final',
    buyerIdNumber: '',
  });
  const [accionEnCurso, setAccionEnCurso] = useState<number>();
  // Resultado de la ÚLTIMA venta confirmada (PR 8.3): reemplaza el aviso fijo "vendida y facturada" — el
  // panel muestra el estado real (autorizada/pendiente/rechazada) y se sondea solo hasta que se resuelve.
  const [resultadoVenta, setResultadoVenta] = useState<ResultadoVenta>();
  const proximaResultadoKey = useRef(0);

  const [cancelacionAbierta, setCancelacionAbierta] = useState<number>();
  const [motivoCancelacion, setMotivoCancelacion] = useState('');

  async function recargar(): Promise<void> {
    const [cat, stock, lista] = await Promise.all([listarCatalogoFarmacia(), listarStockItemsFarmacia(), listarOrdenesFarmacia()]);
    setCatalogo(cat.filter((i) => i.active));
    setDisponibles(stock);
    setOrdenes(lista.orders);
    setDisponible(lista.disponible);
  }

  useEffect(() => {
    void recargar();
    listarPacientesHce()
      .then(setPacientes)
      .catch(() => setPacientes([]));
  }, []);

  // Llegada desde ColaRecetas (?paciente=<medplumId>&receta=<id>): preselecciona
  // el paciente en cuanto la lista carga — una sola vez.
  useEffect(() => {
    if (prefiltrado.current || pacientes.length === 0) return;
    const medplumId = searchParams.get('paciente');
    if (!medplumId) return;
    const p = pacientes.find((x) => x.medplumPatientId === medplumId);
    if (!p) return;
    prefiltrado.current = true;
    void elegirPaciente(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pacientes]);

  const disponiblePorItem = new Map(disponibles.map((d) => [d.catalogItemId, d.disponible]));
  const catalogoPorId = new Map((catalogo ?? []).map((i) => [i.id, i]));

  const lineasValidas = lineas
    .map((l) => ({ itemId: l.catalogItemId, quantity: Number(l.cantidad), medicationRequestRef: l.medicationRequestRef }))
    .filter((l) => Number.isInteger(l.quantity) && l.quantity > 0);
  const totalEstimadoCents = lineasValidas.reduce((suma, l) => {
    const item = catalogoPorId.get(l.itemId);
    return suma + (item ? item.amountCents * l.quantity : 0);
  }, 0);

  const textoItem = busquedaItem.trim().toLowerCase();
  const enCarrito = new Set(lineas.map((l) => l.catalogItemId));
  const resultadosItem =
    textoItem.length === 0
      ? []
      : (catalogo ?? [])
          .filter((i) => !enCarrito.has(i.id) && (coincideCatalogo(i.name.toLowerCase(), textoItem) || i.code.toLowerCase().includes(textoItem)))
          .slice(0, 8);

  function agregarItem(item: CatalogoItemFarmacia, cantidadInicial = '1'): void {
    setLineas((prev) => [...prev, { catalogItemId: item.id, cantidad: cantidadInicial, medicationRequestRef: recetaEnCurso }]);
    setBusquedaItem('');
    setRecetaEnCurso(undefined);
  }

  function quitarItem(catalogItemId: number): void {
    setLineas((prev) => prev.filter((l) => l.catalogItemId !== catalogItemId));
  }

  function cambiarCantidad(catalogItemId: number, cantidad: string): void {
    setLineas((prev) => prev.map((l) => (l.catalogItemId === catalogItemId ? { ...l, cantidad } : l)));
  }

  /** Clic en una receta pendiente: precarga el buscador con su nombre — el farmacéutico confirma el ítem exacto del catálogo, la cantidad ya viene de lo prescrito. */
  function elegirReceta(receta: MedicationRequest): void {
    setBusquedaItem(receta.medicationCodeableConcept?.text ?? '');
    setRecetaEnCurso(`MedicationRequest/${receta.id}`);
  }

  const textoBusqueda = busquedaPaciente.trim().toLowerCase();
  const coincidencias =
    textoBusqueda.length === 0
      ? []
      : pacientes.filter((p) => p.nombre.toLowerCase().includes(textoBusqueda) || p.idNumber.includes(textoBusqueda)).slice(0, 8);

  async function elegirPaciente(p: PacienteHce): Promise<void> {
    setPaciente(p);
    setBusquedaPaciente('');
    if (!patientPhone.trim()) setPatientPhone(p.phone || '');
    setPerfiles(await listarPerfilesFacturacion(p.patientId));
  }

  function quitarPaciente(): void {
    setPaciente(undefined);
    setPerfiles([]);
  }

  function usarPerfil(perfil: PerfilFacturacion): void {
    setBuyerIdType(perfil.idType);
    setBuyerIdNumber(perfil.idNumber);
  }

  async function crear(): Promise<void> {
    if (lineasValidas.length === 0) return;
    setCreando(true);
    setError(undefined);
    setAviso(undefined);
    try {
      const r = await crearOrdenFarmacia({
        patientPhone: patientPhone.trim() || undefined,
        buyerIdType,
        buyerIdNumber: buyerIdType === 'consumidor_final' ? undefined : buyerIdNumber.trim(),
        lines: lineasValidas,
        patientId: paciente?.patientId,
      });
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setAviso(t('farmaciaApp.ordenes.creada', { monto: (r.datos.totalCents / 100).toFixed(2) }));
      setLineas([]);
      setBuyerIdNumber('');
      setPatientPhone('');
      await recargar();
    } finally {
      setCreando(false);
    }
  }

  async function firmar(orden: OrdenFarmacia): Promise<void> {
    setAccionEnCurso(orden.id);
    setError(undefined);
    try {
      const r = await firmarOrdenFarmacia(orden.id);
      if (!r.ok) setError(r.error);
      await recargar();
    } finally {
      setAccionEnCurso(undefined);
    }
  }

  async function aplicar(orden: OrdenFarmacia): Promise<void> {
    setAccionEnCurso(orden.id);
    setError(undefined);
    try {
      const r = await aplicarOrdenFarmacia(orden.id);
      if (!r.ok) setError(r.error);
      await recargar();
    } finally {
      setAccionEnCurso(undefined);
    }
  }

  function abrirVenta(orden: OrdenFarmacia): void {
    setVentaAbierta(orden.id);
    setPanelVenta({
      comoPaga: 'particular',
      metodoParticular: 'cash',
      buyerName: '',
      montoRecibido: '',
      buyerIdType: orden.buyerIdType,
      buyerIdNumber: orden.buyerIdNumber,
    });
    setError(undefined);
  }

  const montoRecibidoCents = Math.round(Number((panelVenta.montoRecibido || '0').replace(',', '.')) * 100);
  const ordenEnVenta = ordenes?.find((o) => o.id === ventaAbierta);
  const vueltoCents = ordenEnVenta ? montoRecibidoCents - ordenEnVenta.totalCents : 0;

  async function confirmarVenta(orden: OrdenFarmacia): Promise<void> {
    if (panelVenta.comoPaga === 'particular' && panelVenta.metodoParticular === 'cash' && montoRecibidoCents < orden.totalCents) {
      setError(t('farmaciaApp.ordenes.montoInsuficiente'));
      return;
    }
    setAccionEnCurso(orden.id);
    setError(undefined);
    setAviso(undefined);
    try {
      const r = await venderOrdenFarmacia(orden.id, {
        comoPaga: panelVenta.comoPaga,
        metodoParticular: panelVenta.comoPaga === 'particular' ? panelVenta.metodoParticular : undefined,
        buyerName: panelVenta.buyerName.trim() || undefined,
        // Sobrescribe la identificación congelada en la orden — la salida
        // real ante un consumidor final que supera USD 50 sin cédula.
        buyerIdType: panelVenta.buyerIdType,
        buyerIdNumber: panelVenta.buyerIdType === 'consumidor_final' ? undefined : panelVenta.buyerIdNumber.trim(),
        patientId: orden.patientId ?? undefined,
      });
      if (!r.ok) {
        setError(r.error);
        return;
      }
      const vueltoTexto =
        panelVenta.comoPaga === 'particular' && panelVenta.metodoParticular === 'cash' && vueltoCents > 0
          ? t('farmaciaApp.resultadoVenta.vueltoAviso', { monto: (vueltoCents / 100).toFixed(2) })
          : '';
      proximaResultadoKey.current += 1;
      setResultadoVenta({
        key: proximaResultadoKey.current,
        orderId: orden.id,
        totalCents: r.datos.totalCents,
        printableHtml: r.datos.printableHtml,
        factura: r.datos.factura,
        vueltoTexto,
        avisoDispensacion: r.datos.avisoDispensacion,
      });
      setVentaAbierta(undefined);
      await recargar();
    } finally {
      setAccionEnCurso(undefined);
    }
  }

  function abrirCancelacion(orden: OrdenFarmacia): void {
    setCancelacionAbierta(orden.id);
    setMotivoCancelacion('');
    setError(undefined);
  }

  /** Cualquier orden que no esté vendida — la salida real ante el tope de USD 50 sin cédula es corregir la cédula al vender o cancelar (Fase 8). */
  async function cancelarOrden(orden: OrdenFarmacia): Promise<void> {
    const motivo = motivoCancelacion.trim();
    if (!motivo) {
      setError(t('farmaciaApp.ordenes.motivoCancelacionRequerido'));
      return;
    }
    setAccionEnCurso(orden.id);
    setError(undefined);
    setAviso(undefined);
    try {
      const r = await cancelarOrdenFarmacia(orden.id, motivo);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setAviso(t('farmaciaApp.ordenes.cancelada'));
      setCancelacionAbierta(undefined);
      await recargar();
    } finally {
      setAccionEnCurso(undefined);
    }
  }

  if (catalogo === undefined || ordenes === undefined) {
    return <p className="text-sm text-hce-muted">{t('farmaciaApp.cargando')}</p>;
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3 rounded-lg border border-border p-4">
        <p className="text-sm font-semibold text-hce-ink">{t('farmaciaApp.ordenes.nuevaTitulo')}</p>

        <div>
          <Label className="mb-1 block text-xs">{t('farmaciaApp.venta.paciente')}</Label>
          {paciente ? (
            <div className="flex items-center justify-between gap-3 rounded-md bg-hce-paper px-3 py-2 text-sm">
              <span>
                <span className="text-hce-ink">{paciente.nombre}</span>
                <span className="text-hce-muted"> · {paciente.idNumber}</span>
              </span>
              <Button type="button" size="sm" variant="ghost" onClick={quitarPaciente}>
                {t('farmaciaApp.venta.quitarPaciente')}
              </Button>
            </div>
          ) : (
            <div className="relative">
              <Input value={busquedaPaciente} onChange={(e) => setBusquedaPaciente(e.target.value)} placeholder={t('farmaciaApp.venta.buscarPacientePlaceholder')} />
              {coincidencias.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-md border border-border bg-white shadow-md">
                  {coincidencias.map((p) => (
                    <button key={p.patientId} type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-hce-paper" onClick={() => void elegirPaciente(p)}>
                      <span className="block text-hce-ink">{p.nombre}</span>
                      <span className="block text-xs text-hce-muted">{p.idNumber}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {paciente && perfiles.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1.5">
              <span className="self-center text-xs text-hce-muted">{t('farmaciaApp.venta.perfilesGuardados')}</span>
              {perfiles.map((perfil) => (
                <button key={perfil.id} type="button" onClick={() => usarPerfil(perfil)} className="rounded-full border border-border px-2.5 py-1 text-xs text-hce-ink hover:bg-hce-paper">
                  {perfil.name} · {perfil.idNumber}
                </button>
              ))}
            </div>
          )}
          {paciente && recetasPendientes.length > 0 && (
            <div className="space-y-1 pt-2">
              <span className="text-xs text-hce-muted">{t('farmaciaApp.ordenes.recetasPendientes')}</span>
              <div className="divide-y divide-border rounded-md border border-border">
                {recetasPendientes.map((receta) => (
                  <button
                    key={receta.id}
                    type="button"
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-hce-paper"
                    onClick={() => elegirReceta(receta)}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-hce-ink">{receta.medicationCodeableConcept?.text ?? t('farmaciaApp.cola.sinNombre')}</span>
                      {receta.dispenseRequest?.quantity && (
                        <span className="block text-xs text-hce-muted">
                          {receta.dispenseRequest.quantity.value} {receta.dispenseRequest.quantity.unit ?? ''}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-xs font-medium text-hce-pine">{t('farmaciaApp.ordenes.agregarReceta')}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {catalogo.length === 0 ? (
          <p className="text-sm text-hce-muted">{t('farmaciaApp.venta.sinCatalogo')}</p>
        ) : (
          <div className="relative">
            <Label className="mb-1 block text-xs">{t('farmaciaApp.venta.agregarItem')}</Label>
            <Input value={busquedaItem} onChange={(e) => setBusquedaItem(e.target.value)} placeholder={t('farmaciaApp.venta.buscarItemPlaceholder')} />
            {recetaEnCurso && <p className="mt-1 text-xs text-hce-pine">{t('farmaciaApp.ordenes.confirmeItemReceta')}</p>}
            {resultadosItem.length > 0 && (
              <div className="absolute z-10 mt-1 w-full rounded-md border border-border bg-white shadow-md">
                {resultadosItem.map((item) => (
                  <button key={item.id} type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-hce-paper" onClick={() => agregarItem(item)}>
                    <span className="block text-hce-ink">{item.name}</span>
                    <span className="block text-xs text-hce-muted">
                      {formatoUsd(item.amountCents)} · {t('farmaciaApp.venta.disponible', { cantidad: disponiblePorItem.get(item.id) ?? 0 })}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {lineas.length === 0 ? (
          <p className="text-sm text-hce-muted">{t('farmaciaApp.venta.carritoVacio')}</p>
        ) : (
          <div className="divide-y divide-border rounded-lg border border-border">
            {lineas.map((linea) => {
              const item = catalogoPorId.get(linea.catalogItemId);
              if (!item) return null;
              return (
                <div key={linea.catalogItemId} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-hce-ink">{item.name}</span>
                    <span className="block text-xs text-hce-muted">
                      {formatoUsd(item.amountCents)}
                      {linea.medicationRequestRef && ` · ${t('farmaciaApp.ordenes.deReceta')}`}
                    </span>
                  </span>
                  <Input
                    type="number"
                    min={1}
                    value={linea.cantidad}
                    onChange={(e) => cambiarCantidad(linea.catalogItemId, e.target.value)}
                    className="h-9 w-20 shrink-0"
                    aria-label={t('farmaciaApp.venta.cantidadDe', { nombre: item.name })}
                  />
                  <button
                    type="button"
                    onClick={() => quitarItem(linea.catalogItemId)}
                    className="shrink-0 rounded-md p-1.5 text-hce-muted hover:bg-hce-paper hover:text-red-600"
                    aria-label={t('farmaciaApp.venta.quitarItem', { nombre: item.name })}
                  >
                    <X className="size-4" aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <div>
            <Label className="mb-1 block text-xs">{t('farmaciaApp.venta.tipoComprador')}</Label>
            <Select value={buyerIdType} onValueChange={(v) => setBuyerIdType(v as BuyerIdTypeFarmacia)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="consumidor_final">{t('farmaciaApp.venta.consumidorFinal')}</SelectItem>
                <SelectItem value="cedula">{t('farmaciaApp.venta.cedula')}</SelectItem>
                <SelectItem value="ruc">{t('farmaciaApp.venta.ruc')}</SelectItem>
                <SelectItem value="pasaporte">{t('farmaciaApp.venta.pasaporte')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {buyerIdType !== 'consumidor_final' && (
            <div>
              <Label className="mb-1 block text-xs">{t('farmaciaApp.venta.numeroId')}</Label>
              <Input value={buyerIdNumber} onChange={(e) => setBuyerIdNumber(e.target.value)} />
            </div>
          )}
          <div>
            <Label className="mb-1 block text-xs">{t('farmaciaApp.venta.telefono')}</Label>
            <Input value={patientPhone} onChange={(e) => setPatientPhone(e.target.value)} />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-1">
          <span className="text-sm text-hce-muted">{t('farmaciaApp.venta.total', { monto: (totalEstimadoCents / 100).toFixed(2) })}</span>
          <Button type="button" disabled={creando || lineasValidas.length === 0} onClick={() => void crear()}>
            {creando ? t('farmaciaApp.ordenes.creando') : t('farmaciaApp.ordenes.crear')}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {aviso && (
        <Alert>
          <AlertDescription>{aviso}</AlertDescription>
        </Alert>
      )}

      {resultadoVenta && <ResultadoVentaFarmacia resultado={resultadoVenta} onCerrar={() => setResultadoVenta(undefined)} />}

      <div className="space-y-2">
        <p className="text-sm font-semibold text-hce-ink">{t('farmaciaApp.ordenes.historialTitulo')}</p>
        {!disponible ? (
          <p className="text-sm text-hce-muted">{t('farmaciaApp.noDisponible')}</p>
        ) : ordenes.length === 0 ? (
          <p className="text-sm text-hce-muted">{t('farmaciaApp.ordenes.vacio')}</p>
        ) : (
          <div className="divide-y divide-border rounded-lg border border-border">
            {ordenes.map((orden) => (
              <div key={orden.id} className="space-y-2 px-3 py-2.5 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="min-w-0 flex-1">
                    <span className="block text-hce-ink">
                      #{orden.id} · {formatoUsd(orden.totalCents)} · {t(`farmaciaApp.ordenes.estado.${orden.status}`)}
                    </span>
                    <span className="block text-xs text-hce-muted">
                      {orden.lines.map((l) => l.description).join(', ') || t('farmaciaApp.ordenes.sinLineas')}
                    </span>
                  </span>
                  <span className="flex shrink-0 flex-wrap items-center gap-1.5">
                    {orden.status === 'draft' && (
                      <Button size="sm" variant="secondary" disabled={accionEnCurso === orden.id} onClick={() => void firmar(orden)}>
                        {t('farmaciaApp.ordenes.firmar')}
                      </Button>
                    )}
                    {orden.status !== 'invoiced' && orden.status !== 'cancelled' && !orden.stockAplicado && (
                      <Button size="sm" variant="secondary" disabled={accionEnCurso === orden.id} onClick={() => void aplicar(orden)}>
                        {t('farmaciaApp.ordenes.aplicar')}
                      </Button>
                    )}
                    {orden.status === 'signed' && (
                      <Button size="sm" disabled={accionEnCurso === orden.id} onClick={() => abrirVenta(orden)}>
                        {t('farmaciaApp.ordenes.vender')}
                      </Button>
                    )}
                    {(orden.status === 'draft' || orden.status === 'signed') && (
                      <Button size="sm" variant="destructive" disabled={accionEnCurso === orden.id} onClick={() => abrirCancelacion(orden)}>
                        {t('farmaciaApp.ordenes.cancelarOrden')}
                      </Button>
                    )}
                  </span>
                </div>

                {ventaAbierta === orden.id && (
                  <div className="flex flex-wrap items-end gap-2 rounded-md bg-hce-paper p-2.5">
                    <div>
                      <label className="mb-1 block text-xs text-hce-muted">{t('farmaciaApp.ordenes.comoPaga')}</label>
                      <Select value={panelVenta.comoPaga} onValueChange={(v) => setPanelVenta((prev) => ({ ...prev, comoPaga: v as ComoPaga }))}>
                        <SelectTrigger className="h-9 w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="particular">{t('farmaciaApp.ordenes.particular')}</SelectItem>
                          <SelectItem value="copago">{t('farmaciaApp.ordenes.copago')}</SelectItem>
                          <SelectItem value="reembolso">{t('farmaciaApp.ordenes.reembolso')}</SelectItem>
                          <SelectItem value="carta">{t('farmaciaApp.ordenes.carta')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {panelVenta.comoPaga === 'particular' && (
                      <div>
                        <label className="mb-1 block text-xs text-hce-muted">{t('farmaciaApp.ordenes.formaPago')}</label>
                        <Select
                          value={panelVenta.metodoParticular}
                          onValueChange={(v) => setPanelVenta((prev) => ({ ...prev, metodoParticular: v as FormaPagoParticular }))}
                        >
                          <SelectTrigger className="h-9 w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cash">{t('farmaciaApp.ordenes.efectivo')}</SelectItem>
                            <SelectItem value="card">{t('farmaciaApp.ordenes.tarjeta')}</SelectItem>
                            <SelectItem value="transfer">{t('farmaciaApp.ordenes.transferencia')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {panelVenta.comoPaga === 'particular' && panelVenta.metodoParticular === 'cash' && (
                      <div>
                        <label className="mb-1 block text-xs text-hce-muted">{t('farmaciaApp.ordenes.montoRecibido')}</label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          className="h-9 w-28"
                          value={panelVenta.montoRecibido}
                          onChange={(e) => setPanelVenta((prev) => ({ ...prev, montoRecibido: e.target.value }))}
                        />
                        {panelVenta.montoRecibido && (
                          <p className="mt-1 text-xs text-hce-muted">
                            {vueltoCents >= 0
                              ? t('farmaciaApp.ordenes.vuelto', { monto: (vueltoCents / 100).toFixed(2) })
                              : t('farmaciaApp.ordenes.montoInsuficiente')}
                          </p>
                        )}
                      </div>
                    )}
                    <div className="min-w-40 flex-1">
                      <label className="mb-1 block text-xs text-hce-muted">{t('farmaciaApp.venta.nombreComprador')}</label>
                      <Input value={panelVenta.buyerName} onChange={(e) => setPanelVenta((prev) => ({ ...prev, buyerName: e.target.value }))} />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-hce-muted">{t('farmaciaApp.venta.tipoComprador')}</label>
                      <Select
                        value={panelVenta.buyerIdType}
                        onValueChange={(v) => setPanelVenta((prev) => ({ ...prev, buyerIdType: v as BuyerIdTypeFarmacia }))}
                      >
                        <SelectTrigger className="h-9 w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="consumidor_final">{t('farmaciaApp.venta.consumidorFinal')}</SelectItem>
                          <SelectItem value="cedula">{t('farmaciaApp.venta.cedula')}</SelectItem>
                          <SelectItem value="ruc">{t('farmaciaApp.venta.ruc')}</SelectItem>
                          <SelectItem value="pasaporte">{t('farmaciaApp.venta.pasaporte')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {panelVenta.buyerIdType !== 'consumidor_final' && (
                      <div>
                        <label className="mb-1 block text-xs text-hce-muted">{t('farmaciaApp.venta.numeroId')}</label>
                        <Input
                          className="h-9 w-40"
                          value={panelVenta.buyerIdNumber}
                          onChange={(e) => setPanelVenta((prev) => ({ ...prev, buyerIdNumber: e.target.value }))}
                        />
                      </div>
                    )}
                    <Button size="sm" disabled={accionEnCurso === orden.id} onClick={() => void confirmarVenta(orden)}>
                      {t('farmaciaApp.comprobantes.confirmar')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setVentaAbierta(undefined)}>
                      {t('farmaciaApp.catalogo.cancelar')}
                    </Button>
                  </div>
                )}

                {cancelacionAbierta === orden.id && (
                  <div className="flex flex-wrap items-end gap-2 rounded-md bg-hce-paper p-2.5">
                    <div className="min-w-48 flex-1">
                      <label className="mb-1 block text-xs text-hce-muted">{t('farmaciaApp.ordenes.motivoCancelacion')}</label>
                      <Input value={motivoCancelacion} onChange={(e) => setMotivoCancelacion(e.target.value)} />
                    </div>
                    <Button size="sm" variant="destructive" disabled={accionEnCurso === orden.id} onClick={() => void cancelarOrden(orden)}>
                      {accionEnCurso === orden.id ? t('farmaciaApp.ordenes.cancelando') : t('farmaciaApp.comprobantes.confirmar')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setCancelacionAbierta(undefined)}>
                      {t('farmaciaApp.catalogo.cancelar')}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
